import * as React from 'react';
import {
  ForwardedRef,
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DataRequestVO,
  DataResponseVO,
  DataSortVO,
} from '@mc/data-filter-react';
import * as Classes from '../common/classes';
import {
  AutoSizer,
  InfiniteLoader,
  SizeInfo,
  Grid,
  IndexRange,
  ScrollSync,
  // @ts-ignore
} from 'react-virtualized';
import classNames from 'classnames';
import { InfiniteLoaderChildProps } from 'react-virtualized/dist/es/InfiniteLoader';
import {
  CheckStatus,
  CommonRenderer,
  IData,
  VirtualDataRef,
} from '../virtual-data/VirtualData';
import { useVirtualData } from '../virtual-data/useVirtualData';
import {
  BaseTableProps,
  getFormattedColumns,
  getFormattedValue,
  TableColumn,
} from '../table/TableProps';
import { GridCellProps, RenderedSection } from 'react-virtualized/dist/es/Grid';
import { useCurrentTheme } from '../themes';
import { Deleted } from '../deleted/Deleted';
import { Skeleton } from '../skeleton/Skeleton';
import { CheckboxButton } from '../checkbox/CheckboxButton';
import { Icon } from '../icon/Icon';
import { useCheckIds } from '../rows-data/useCheckIds';
import { Spinner } from '../spinner/Spinner';
import { NoData } from '../no-data/NoData';
import { mcSortAsc, mcSortDesc } from '@mc/react-ui-icons';
import {
  getTableColumnCellValue,
  getTableFooterColumnCellValue,
} from './dataUtils';
import { DataTableMode } from '../common/props';
import { FilterType } from '../filter/types';
import { ColumnFilter } from './ColumnFilter';
import { Link } from '../link/Link';
import { Tooltip } from '../tooltip/Tooltip';
const emptyArr: any[] = [];

// @ts-ignore
const fakesetCheckedIds = (ids: any[]) => {};

export type DataTableColumnFilters<FORM> = {
  formFilter: FORM;
  setFilter: (formData: FORM, queryData: FORM) => void;
  type?: FilterType;
};

export type BaseDataTableProps<
  DATA extends IData<ID>,
  ID,
  FORM,
  DATA_FOOTER,
> = {
  footerHeight?: number;
  footerData?: DATA_FOOTER;
  noVerticalScroll?: boolean;
  getItemId?: (item: DATA) => ID;
  checkedIds?: ID[];
  setCheckedIds?: (ids: ID[]) => void;
  highlightId?: ID;
  rowHeight?: number;
  headerHeight?: number;
  checkable?: boolean;
  lastVisitedRowIndex?: number;
  setLastVisitedRowIndex?: (id: number) => void;
  /**
   * potřeba pouze v případě, že je DataTable používána jako součást PaginationTable
   */
  pageNumber?: number;
  /**
   * Pokud true, je možné pomocí kávesy shift provádět řazení dle více sloupců.
   */
  multisort?: boolean;
  defaultColumnWidth?: number;
  extra?: (props: CommonRenderer<ID>) => ReactNode;
  sort?: DataSortVO[];
  onSort?: (sort: DataSortVO[]) => void;
  throttleTime?: number;
  mode?: DataTableMode;
  filters?: DataTableColumnFilters<FORM>;
};

type Props<DATA extends IData<ID>, FILTER, ID, DATA_FOOTER> = {
  // Nadpis tabulky - ve výchozím stavu se zobrazuje velikostí cardTitle
  title?: React.ReactNode;
  totalElements?: number;
  ids?: ID[];
  api: (request: DataRequestVO<FILTER>) => Promise<DataResponseVO<DATA>>;
  initialCache?: DATA[];
} & BaseDataTableProps<DATA, ID, FILTER, DATA_FOOTER> &
  BaseTableProps<DATA>;

type SortInfo = {
  sort: DataSortVO;
  depth: number;
};

const DataTableIn = <
  DATA extends IData<ID>,
  FILTER extends object = {},
  ID extends unknown = {},
  DATA_FOOTER extends object = {},
>(
  {
    className,
    ids,
    checkedIds,
    setCheckedIds,
    highlightId,
    rowHeight,
    totalElements,
    throttleTime,
    initialCache,
    api,
    checkable,
    getItemId,
    columns,
    defaultColumnWidth = 120,
    rowDisableHover,
    headerHeight,
    sort,
    onSort,
    extra,
    title,
    pageNumber,
    multisort,
    mode = 'default',
    noVerticalScroll,
    filters,
    footerData,
    footerHeight,
    setLastVisitedRowIndex,
    lastVisitedRowIndex,
    getRowProps = () => ({}),
  }: Props<DATA, FILTER, ID, DATA_FOOTER>,
  ref: ForwardedRef<VirtualDataRef<ID>>
) => {
  const realIds = ids || emptyArr;
  const realTotalElements = totalElements || 0;
  const loading = !ids || totalElements === undefined || totalElements === null;
  const hasData = realTotalElements > 0;
  const virtualData = useVirtualData<DATA, FILTER, ID>({
    ids: realIds,
    ref,
    api,
    getItemId,
    throttleTime,
    initialCache,
  });

  const checkIds = useCheckIds<ID>(
    realIds,
    checkedIds || emptyArr,
    setCheckedIds || fakesetCheckedIds
  );

  const commonsProps: CommonRenderer<ID> = {
    headerProps: {
      checkedStatus: checkIds.checkedStatus,
      onToggleCheckAll: checkIds.onToggleCheckAll,
      totalElements: realTotalElements,
      checkable: !!checkable,
      disabled: loading,
      extraProps: {
        totalElements,
        title,
      },
    },
    checkAll: checkIds.checkAll,
    checkedIds: checkedIds || emptyArr,
  };
  const theme = useCurrentTheme();
  const resultHeaderHeight = useMemo<number>(() => {
    if (headerHeight !== undefined) {
      return headerHeight;
    } else {
      return theme.dataTableHeaderHeight;
    }
  }, [theme]);
  const resultRowHeight = useMemo<number>(() => {
    if (rowHeight !== undefined) {
      return rowHeight;
    } else {
      return theme.dataTableRowHeight;
    }
  }, [theme]);
  const isFooter = useMemo(() => columns.some((x) => x.footer), [columns]);
  const resultFooterHeight = useMemo<number>(() => {
    if (!isFooter) {
      return 0;
    }
    if (footerHeight !== undefined) {
      return footerHeight;
    } else {
      return resultRowHeight;
    }
  }, [isFooter]);

  const formattedColumns = useMemo(() => {
    let resCols = getFormattedColumns(columns);
    if (checkable) {
      resCols = [
        {
          header: (
            <CheckboxButton
              // @ts-ignore
              onChange={() => checkIds.onToggleCheckAll()}
              disabled={!hasData}
              indeterminate={
                checkIds.checkedStatus === CheckStatus.SOME_CHECKED
              }
              checked={
                checkIds.checkedStatus === CheckStatus.ALL_CHECKED
                  ? true
                  : checkIds.checkedStatus === CheckStatus.ALL_UNCHECKED
                    ? false
                    : undefined
              }
            />
          ),
          width:
            theme.checkboxButtonSize +
            (theme.tableCellHorizontalPadding * 2) / 2, // pro tento sloupec chceme poloviční padding, protože checkbox už nějaký má sám o sobě
          cell: ({ rowIndex }) => {
            const rowRenderProps = virtualData.getRowRenderProps(rowIndex);
            rowRenderProps.onCheck = () => checkIds.onCheck(rowRenderProps.id);
            rowRenderProps.checkable = checkable;
            rowRenderProps.checked = checkIds.isChecked(rowRenderProps.id);
            rowRenderProps.highlight = highlightId === rowRenderProps.id;

            return (
              <div onClick={(e) => e.stopPropagation()}>
                <CheckboxButton
                  // @ts-ignore
                  onChange={rowRenderProps.onCheck}
                  // disabled={disabled}
                  checked={rowRenderProps.checked}
                />
              </div>
            );
          },
        },
        // @ts-ignore
        ...resCols,
      ];
    }
    return resCols;
  }, [theme, columns, checkable, virtualData]);

  const renderRowBodyCell = (
    { rowIndex, parent, key, style }: GridCellProps,
    columnsWidth: { [key: number]: number }
  ) => {
    const cells: ReactNode[] = [];
    let rowRenderProps = virtualData.getRowRenderProps(rowIndex);
    rowRenderProps.checked = checkIds.isChecked(rowRenderProps.id);
    rowRenderProps.highlight = highlightId === rowRenderProps.id;

    let x = 0;
    for (let colIndex = 0; colIndex < formattedColumns.length; colIndex++) {
      const cellStyle: React.CSSProperties = {
        position: 'absolute',
        height: rowRenderProps.highlight
          ? resultRowHeight - 1
          : resultRowHeight,
        width: columnsWidth[colIndex],
        left: x,
        top: 0,
        paddingRight:
          colIndex === 0 && rowRenderProps.highlight ? 1 : undefined,
        paddingLeft:
          colIndex === formattedColumns.length - 1 && rowRenderProps.highlight
            ? 1 +
              (formattedColumns[formattedColumns.length - 1].type === 'action'
                ? 6
                : 1)
            : undefined,
      };
      x += columnsWidth[colIndex];

      cells.push(
        renderBodyCell({
          rowIndex,
          columnIndex: colIndex,
          key: `${key}_${rowIndex}_${colIndex}`,
          isScrolling: false,
          isVisible: true,
          parent,
          style: cellStyle,
        })
      );
    }

    const customRowProps = getRowProps({ row: rowRenderProps.row, rowIndex });

    const itemClassName = classNames(
      Classes.DATA_TABLE_ROW,
      Classes.HOVER_CONTAINER,
      customRowProps?.className,
      {
        [Classes.HOVER_ITEM_CONTAINER]: !rowDisableHover,
        [Classes.CLICK_CONTAINER]:
          customRowProps?.onClick || customRowProps?.link,
        [Classes.CHECKED_CONTAINER]: rowRenderProps.checked,
        [Classes.HIGHLIGHT_CONTAINER]: rowRenderProps.highlight,
      }
    );
    const itemStyle = {
      ...customRowProps?.style,
      ...style,
    };

    if (customRowProps?.link && rowRenderProps?.row) {
      return (
        <Link
          key={rowIndex}
          {...customRowProps?.link}
          className={itemClassName}
          style={itemStyle}
        >
          <>{cells}</>
        </Link>
      );
    } else {
      return (
        <div
          key={rowIndex}
          className={itemClassName}
          style={itemStyle}
          {...(rowRenderProps?.row && { onClick: customRowProps?.onClick })}
        >
          {cells}
        </div>
      );
    }
  };
  const renderRowFooterCell = (
    { rowIndex, parent, key }: GridCellProps,
    columnsWidth: { [key: number]: number }
  ) => {
    const cells: ReactNode[] = [];

    const colCount = noVerticalScroll
      ? formattedColumns.length
      : formattedColumns.length + 1;
    let x = 0;
    for (let colIndex = 0; colIndex < colCount; colIndex++) {
      const cellStyle: React.CSSProperties = {
        position: 'absolute',
        height: footerHeight ?? resultFooterHeight,
        width: columnsWidth[colIndex],
        left: x,
        top: 0,
      };
      x += columnsWidth[colIndex];

      cells.push(
        renderFooterCell({
          rowIndex,
          columnIndex: colIndex,
          key: `${key}_${rowIndex}_${colIndex}`,
          isScrolling: false,
          isVisible: true,
          parent,
          style: cellStyle,
        })
      );
    }

    return <>{cells}</>;
  };

  const renderRowHeaderCell = (
    { rowIndex, parent, key }: GridCellProps,
    columnsWidth: { [key: number]: number }
  ) => {
    const cells: ReactNode[] = [];

    const colCount = noVerticalScroll
      ? formattedColumns.length
      : formattedColumns.length + 1;
    let x = 0;
    for (let colIndex = 0; colIndex < colCount; colIndex++) {
      const cellStyle: React.CSSProperties = {
        position: 'absolute',
        height: resultHeaderHeight,
        width: columnsWidth[colIndex],
        left: x,
        top: 0,
      };
      x += columnsWidth[colIndex];

      cells.push(
        renderHeaderCell({
          rowIndex,
          columnIndex: colIndex,
          key: `${key}_${rowIndex}_${colIndex}`,
          isScrolling: false,
          isVisible: true,
          parent,
          style: cellStyle,
        })
      );
    }

    return <>{cells}</>;
  };

  const renderBodyCell = ({
    rowIndex,
    columnIndex,
    key,
    style,
  }: GridCellProps) => {
    const rowData = virtualData.getRowByIndex(rowIndex);
    const column = formattedColumns[columnIndex];

    const cellValue = getTableColumnCellValue(column, rowData);

    // Tooltip na buňce, může být níže ještě překryt string hodnotou buňky (výsledek volání cell()), ale jen pokud bude shownTitleCanOverride true
    let shownTitle;
    let shownTitleCanOverride = false;
    if (column.title) {
      shownTitle = column.title({
        rowIndex: rowIndex,
        columnIndex: columnIndex,
        // @ts-ignore
        row: rowData,
        column,
        value: cellValue,
        formattedValue: column.type
          ? getFormattedValue(column, cellValue)
          : undefined,
      });
    } else if (column.columnTitle) {
      shownTitle = column.columnTitle;
    } else if (column.accessor) {
      shownTitleCanOverride = true;
      shownTitle = getFormattedValue(column, cellValue) ?? cellValue;

      // Pokud by se stalo, že by hodnotou title byl objekt, tak se title zahodí
      try {
        if (
          shownTitle &&
          typeof shownTitle === 'object' &&
          Object.keys(shownTitle).length > 0
        ) {
          shownTitle = undefined;
        }
      } catch (e) {
        console.log(e);
      }
    } else {
      shownTitleCanOverride = true;
      shownTitle = undefined;
    }

    const cellClassName = classNames(
      column.className,
      Classes.DATA_TABLE_BODY_CELL,
      {
        [Classes.DATA_TABLE_BODY_CHECK_CELL]: checkable && columnIndex === 0,
        [Classes.DATA_TABLE_BODY_ACTION_CELL]: column.type === 'action',
      }
    );

    let cellContent = undefined;

    if (checkable && columnIndex === 0 && column && column.cell) {
      cellContent = column.cell({
        rowIndex: rowIndex,
        columnIndex: columnIndex,
        // @ts-ignore
        row: rowData,
        column,
        value: virtualData.getRowRenderProps(rowIndex).checked,
      });
    } else {
      if (rowData === null) {
        cellContent = (
          <div className={Classes.DATA_TABLE_BODY_CELL_VALUE}>
            <Deleted />
          </div>
        );
      } else if (rowData === undefined) {
        cellContent = (
          <div className={Classes.DATA_TABLE_BODY_CELL_VALUE}>
            {column.type !== 'action' && (
              <Skeleton row={rowIndex} col={columnIndex} />
            )}
          </div>
        );
      } else {
        if (column.cell) {
          let cell = column.cell({
            rowIndex: rowIndex,
            columnIndex: columnIndex,
            row: rowData,
            column,
            value: cellValue,
            formattedValue: column.type
              ? getFormattedValue(column, cellValue)
              : undefined,
          });

          if (shownTitleCanOverride && typeof cell === 'string') {
            shownTitle = cell;
          }

          cellContent = (
            <div className={Classes.DATA_TABLE_BODY_CELL_VALUE}>{cell}</div>
          );
        } else {
          cellContent = (
            <div className={Classes.DATA_TABLE_BODY_CELL_VALUE}>
              {cellValue}
            </div>
          );
        }
      }
    }

    const content = (
      <div
        key={key}
        title={typeof shownTitle === 'string' ? shownTitle : undefined}
        className={cellClassName}
        style={style}
      >
        {cellContent}
      </div>
    );

    if (!shownTitle || typeof shownTitle === 'string') {
      return content;
    } else {
      return <Tooltip trigger={content}>{shownTitle}</Tooltip>;
    }
  };

  // const [grid, setGrid] = useState<Grid | null>(null);
  // const getScrollbarWidth = () => {
  //   if (grid) {
  //     const Grid = findDOMNode(grid);
  //     // @ts-ignore
  //     const clientWidth = Grid.clientWidth || 0;
  //     // @ts-ignore
  //     const offsetWidth = Grid.offsetWidth || 0;
  //     return offsetWidth - clientWidth;
  //   }
  //
  //   return 0;
  // };

  // let scrollbarWidth = getScrollbarWidth();
  const scrollbarWidth = noVerticalScroll
    ? 0
    : useCurrentTheme().scrollbarWidth;
  const sortMap = useMemo<{ [key: string]: SortInfo }>(() => {
    const map: { [key: string]: SortInfo } = {};

    if (sort) {
      sort.forEach((x, index) => {
        map[x.field] = {
          sort: x,
          depth: index,
        };
      });
    }

    return map;
  }, [sort]);

  /**
   * Změna řazení.
   * @param columnIndex index sloupce
   * @param column info o sloupci
   * @param shiftKey byla při kliknutí stisknutá klávesa shift?
   */
  const toggleSort = (
    columnIndex: number,
    column: TableColumn<DATA>,
    shiftKey?: boolean
  ) => {
    const field = column.id || column.accessor || `column-${columnIndex}`;
    const sortInfo = sortMap[field];

    let newSort: DataSortVO[];
    if (multisort) {
      if (sortInfo) {
        if (shiftKey) {
          // již se podle sloupce řadí, jen změníme jeho směr
          // @ts-ignore
          newSort = [...sort];
          newSort[sortInfo.depth].asc = !newSort[sortInfo.depth].asc;
        } else {
          newSort = [
            {
              field,
              asc: true,
            },
          ];
        }
      } else {
        if (shiftKey) {
          newSort = [
            // @ts-ignore
            ...sort,
            {
              field,
              asc: true,
            },
          ];
        } else {
          newSort = [
            {
              field,
              asc: true,
            },
          ];
        }
      }
    } else {
      let currAsc = sortInfo && sortInfo.sort.asc;
      newSort = [
        {
          field,
          asc: !currAsc,
        },
      ];
    }

    onSort?.(newSort);
  };
  const renderFooterCell = ({
    rowIndex,
    columnIndex,
    key,
    style,
  }: GridCellProps) => {
    const column = formattedColumns[columnIndex];
    if (!column || !column.footer) {
      return null;
    }
    const cellClassName = classNames(
      column.className,
      Classes.DATA_TABLE_FOOTER_CELL,
      {
        [Classes.DATA_TABLE_BODY_CHECK_CELL]: checkable && columnIndex === 0,
        [Classes.DATA_TABLE_BODY_ACTION_CELL]: column.type === 'action',
      }
    );

    let cellContent = undefined;
    if (column?.footer && !footerData) {
      cellContent = (
        <div className={Classes.DATA_TABLE_FOOTER_CELL_VALUE}>
          {column.type !== 'action' && (
            <Skeleton row={rowIndex} col={columnIndex} />
          )}
        </div>
      );
    } else {
      let cellValue = getTableFooterColumnCellValue(column.footer, footerData);
      if (column.footer?.cell) {
        cellContent = (
          <div className={Classes.DATA_TABLE_FOOTER_CELL_VALUE}>
            {column?.footer?.cell({
              footerData: footerData,
              value: cellValue,
              formattedValue: column.footer.type
                ? getFormattedValue(column.footer, cellValue)
                : undefined,
            })}
          </div>
        );
      } else {
        cellContent = (
          <div className={Classes.DATA_TABLE_FOOTER_CELL_VALUE}>
            {cellValue}
          </div>
        );
      }
    }

    return (
      <div key={key} className={cellClassName} style={style}>
        {cellContent}
      </div>
    );
  };

  const renderHeaderCell = ({ columnIndex, key, style }: GridCellProps) => {
    // Scroolbar width column
    if (columnIndex === formattedColumns.length) {
      return (
        <div
          key={key}
          className={Classes.DATA_TABLE_HEADER_CELL}
          style={style}
        ></div>
      );
    }

    const column = formattedColumns[columnIndex];
    const field = column.id || column.accessor || `column-${columnIndex}`;
    const checkCell = checkable && columnIndex === 0;
    const sortInfo = sortMap[field];
    const hasFilters = column.filters && column.filters.length > 0;

    return (
      <div
        key={key}
        className={classNames(Classes.DATA_TABLE_HEADER_CELL, {
          [Classes.DATA_TABLE_HEADER_CHECK_CELL]: checkCell,
          [Classes.DATA_TABLE_HEADER_CELL_SORTABLE]: column.sortable,
          [Classes.DATA_TABLE_HEADER_CELL_FILTERABLE]: hasFilters,
        })}
        style={style}
        onClick={
          column.sortable && !loading
            ? (e) => {
                if (multisort) {
                }
                document.getSelection()?.removeAllRanges();
                e.stopPropagation();
                e.preventDefault();
                toggleSort(columnIndex, column, e.shiftKey);
              }
            : undefined
        }
      >
        {checkCell ? (
          column.header
        ) : (
          <>
            <div
              className={classNames(Classes.DATA_TABLE_HEADER_CELL_VALUE, {})}
            >
              {column.header}
            </div>
            {hasFilters && filters && (
              <ColumnFilter<DATA, FILTER, ID>
                column={column}
                filters={filters}
              />
            )}
            {column.sortable && (
              <div className={Classes.DATA_TABLE_HEADER_CELL_SORTING}>
                {sortInfo && (
                  <Icon icon={sortInfo.sort.asc ? mcSortAsc : mcSortDesc} />
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const computeColumnsWidth = (
    areaWidth: number,
    columns: TableColumn<DATA>[]
  ) => {
    const res: { [key: number]: number } = {};

    // Zmenšení oblasti o scrollbar
    const areaWidthWithoutScrollbar = areaWidth - scrollbarWidth;

    // Spočtení šířky pevných sloupců
    let fixedWidth = 0;
    columns.forEach((col) => {
      if (col.type === 'action' && col.width === undefined) {
        let size = col.actionSize || 1;
        fixedWidth +=
          size * theme.iconButtonWidth +
          (size - 1) * theme.spacers[1] +
          theme.tableCellHorizontalPadding;
      } else {
        if (typeof col.width === 'number') {
          fixedWidth += col.width;
        } else if (typeof col.width === 'string') {
          if (!col.width.endsWith('%')) {
            fixedWidth += parseFloat(col.width);
          }
        }
      }
    });

    // Kolik zbylo velikosti na sloupce s procentuální šířkou
    let forPercentColsWidth = areaWidthWithoutScrollbar - fixedWidth;
    if (forPercentColsWidth < 0) {
      forPercentColsWidth = areaWidth;
    }

    columns.forEach((col, index) => {
      let colPixelWidth;

      if (col.type === 'action' && col.width === undefined) {
        let size = col.actionSize || 1;
        colPixelWidth =
          size * theme.iconButtonWidth +
          (size - 1) * theme.spacers[1] +
          theme.tableCellHorizontalPadding;
      } else {
        if (typeof col.width === 'number') {
          colPixelWidth = col.width;
        } else if (typeof col.width === 'string') {
          if (col.width.endsWith('%')) {
            colPixelWidth = (forPercentColsWidth * parseFloat(col.width)) / 100;
          } else {
            colPixelWidth = parseInt(col.width);
          }
        }
      }

      res[index] = colPixelWidth || defaultColumnWidth;
    });
    res[columns.length] = scrollbarWidth;

    // console.log(
    //   'area width',
    //   areaWidth,
    //   'area without scrollbar',
    //   areaWidthWithoutScrollbar,
    //   'fixedQidth',
    //   fixedWidth,
    //   'percent width',
    //   forPercentColsWidth,
    //   'res',
    //   res
    // );

    return res;
  };
  const onSectionRendered = (
    props: RenderedSection,
    onRowsRendered: (params: IndexRange) => void
  ) => {
    // console.log('SECTIOn redered', props.rowStartIndex, props.rowStopIndex);
    virtualData.onSectionRendered({
      startIndex: props.rowStartIndex,
      stopIndex: props.rowStopIndex,
    });
    onRowsRendered({
      startIndex: props.rowStartIndex,
      stopIndex: props.rowStopIndex,
    });
  };
  const gridRef = useRef<Grid | null>(null);
  const sourceScrollbarWidth = useCurrentTheme().scrollbarWidth;

  const [scrollToHighlight, setScrollToHighlight] = useState<boolean>(
    Number.isInteger(highlightId)
  );

  const getScrollToHighlight = useCallback(() => {
    if (highlightId && scrollToHighlight && hasData) {
      setScrollToHighlight(false);
      return ids?.indexOf(highlightId);
    } else if (lastVisitedRowIndex) {
      return lastVisitedRowIndex;
    } else {
      return undefined;
    }
  }, [highlightId, scrollToHighlight, ids]);

  useEffect(() => {
    if (gridRef.current && pageNumber && pageNumber > 0) {
      //nejsem si uplně jistej co jsem tady dělal, ale jinak jsem to nevymyslel
      //@ts-ignore  použit ts-ignore, protože funkce tvrdí, že je potřeba předat i scrollLeft ale není
      gridRef.current?.scrollToPosition({ scrollTop: 0 });
    }
  }, [pageNumber]);
  return (
    <div
      className={classNames(Classes.DATA_TABLE_CONTAINER, className, {
        [Classes.DATA_TABLE_MODE_DEFAULT]: mode === 'default',
        [Classes.DATA_TABLE_MODE_INPUT]: mode === 'input',
        [Classes.DATA_TABLE_NO_DATA]: !hasData,
      })}
      style={{ position: 'relative' }}
    >
      {loading && (
        <div
          style={{
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
            zIndex: 1,
          }}
        >
          <Spinner />
        </div>
      )}
      {extra && (
        <div className={classNames(Classes.DATA_TABLE_EXTRA_CONTAINER)}>
          {extra(commonsProps)}
        </div>
      )}
      <div
        className={classNames(Classes.DATA_TABLE, {
          [Classes.DATA_TABLE_NO_VERTICAL_SCROLL]: noVerticalScroll,
        })}
      >
        <ScrollSync>
          {({ onScroll, scrollLeft }) => (
            <AutoSizer disableHeight={noVerticalScroll}>
              {({ width, height }: SizeInfo) => {
                if (noVerticalScroll) {
                  if (footerData) {
                    height =
                      resultHeaderHeight +
                      resultRowHeight * realTotalElements +
                      resultFooterHeight;
                  } else {
                    height =
                      resultHeaderHeight + resultRowHeight * realTotalElements;
                  }
                }

                const columnsWidth = computeColumnsWidth(
                  width,
                  formattedColumns
                );
                let rowColumnWidth = 0;
                Object.values(columnsWidth).forEach((v) => {
                  rowColumnWidth += v;
                });

                rowColumnWidth -= scrollbarWidth;
                const horizontalScrollBarHeight =
                  rowColumnWidth > width ? sourceScrollbarWidth : 0;
                // console.log('rowColumnWidth', rowColumnWidth);
                return (
                  <>
                    <Grid
                      className={classNames(Classes.DATA_TABLE_HEADER)}
                      cellRenderer={(props) =>
                        renderRowHeaderCell(props, columnsWidth)
                      }
                      columnWidth={
                        rowColumnWidth + columnsWidth[formattedColumns.length]
                      }
                      columnCount={1}
                      overscanColumnCount={1}
                      __rerender={virtualData.renderer}
                      height={resultHeaderHeight}
                      width={width}
                      rowCount={1}
                      rowHeight={resultHeaderHeight}
                      scrollLeft={scrollLeft}
                    />
                    {hasData || loading ? (
                      <>
                        <InfiniteLoader
                          ref={virtualData.infiniteLoaderRef}
                          rowCount={realTotalElements}
                          loadMoreRows={virtualData.loadMoreRows}
                          isRowLoaded={virtualData.isRowLoaded}
                          minimumBatchSize={1}
                          threshold={15}
                        >
                          {({
                            onRowsRendered,
                            registerChild,
                          }: InfiniteLoaderChildProps) => (
                            <Grid
                              className={classNames(Classes.DATA_TABLE_CONTENT)}
                              cellRenderer={(props) =>
                                renderRowBodyCell(props, columnsWidth)
                              }
                              columnWidth={rowColumnWidth}
                              columnCount={1}
                              overscanColumnCount={1}
                              __rerender={virtualData.renderer}
                              ref={(ref) => {
                                registerChild(ref);
                                if (ref !== null) {
                                  gridRef.current = ref;
                                }
                              }}
                              onSectionRendered={(props) =>
                                onSectionRendered(props, onRowsRendered)
                              }
                              height={
                                height -
                                resultHeaderHeight -
                                (isFooter && noVerticalScroll
                                  ? resultFooterHeight
                                  : 0)
                              }
                              style={
                                isFooter
                                  ? {
                                      paddingBottom: resultHeaderHeight,
                                    }
                                  : undefined
                              }
                              width={width}
                              rowCount={realTotalElements}
                              rowHeight={resultRowHeight}
                              onScroll={(props) => {
                                let total =
                                  height -
                                  resultHeaderHeight -
                                  scrollbarWidth +
                                  props.scrollTop;
                                const cilovyRadek = Math.round(
                                  total / resultRowHeight
                                );

                                if (setLastVisitedRowIndex) {
                                  setLastVisitedRowIndex(cilovyRadek - 1);
                                }
                                onScroll(props);
                              }}
                              // @ts-ignore
                              scrollToRow={getScrollToHighlight()}
                            />
                          )}
                        </InfiniteLoader>
                        {/*footer*/}
                        {isFooter && (
                          <Grid
                            className={classNames(Classes.DATA_TABLE_FOOTER)}
                            cellRenderer={(props) =>
                              renderRowFooterCell(props, columnsWidth)
                            }
                            columnWidth={
                              rowColumnWidth +
                              columnsWidth[formattedColumns.length]
                            }
                            columnCount={1}
                            overscanColumnCount={1}
                            __rerender={virtualData.renderer}
                            height={resultFooterHeight}
                            width={width - scrollbarWidth}
                            rowCount={1}
                            rowHeight={resultFooterHeight}
                            scrollLeft={scrollLeft}
                            style={{
                              bottom: !noVerticalScroll
                                ? resultFooterHeight + horizontalScrollBarHeight
                                : 0,
                            }}
                          />
                        )}
                      </>
                    ) : (
                      <div
                        //inline styl protože width je z variable
                        style={{ width: noVerticalScroll ? width : '100%' }}
                        className={classNames(Classes.DATA_TABLE_NO_CONTENT, {
                          [Classes.DATA_TABLE_POSITION_ABSOLUTE]:
                            !noVerticalScroll,
                        })}
                      >
                        <NoData />
                      </div>
                    )}
                  </>
                );
              }}
            </AutoSizer>
          )}
        </ScrollSync>
      </div>
    </div>
  );
};

export const DataTable = forwardRef(DataTableIn) as <
  DATA extends IData<ID>,
  FILTER extends object = {},
  ID extends unknown = {},
  DATA_FOOTER extends object = {},
>(
  props: Props<DATA, FILTER, ID, DATA_FOOTER> & {
    ref?: React.ForwardedRef<VirtualDataRef<ID>>;
  }
) => ReturnType<typeof DataTableIn>;
